import type { LayoutNode } from '@vedalib/editor'
import { isNodeV2, type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import React, { useMemo } from 'react'

import EditorElementComponent from 'components/Editor/EditorElements/EditorElementComponent'
import ErrorBoundary from 'components/Editor/EditorElements/decorators/ErrorBoundary'
import Scalable from 'components/uiKit/Scalable'
import { SKELETON_MODE } from 'services/Store/Project/constants'
import type { DeviceMode } from 'services/Store/Project/enums'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { MapType } from 'services/Store/Project/types'

import NodeV2Styled from '../styled/NodeV2Styled'

interface IScaledBlockSkeletonProps {
  node: NodeV2
  parent?: NodeV2 | null
  nodes?: MapType<LayoutNode>
  elements?: MapType<EditorElement>
  deviceMode: DeviceMode
}

const Node: React.FC<IScaledBlockSkeletonProps> = (props) => {
  const { node, nodes, elements, parent = null, deviceMode } = props
  const children = node.children?.map((childId) => nodes?.[childId]).filter(isNodeV2)
  const element = (node.elementId && elements?.[node.elementId]) || null
  const style = useMemo(
    () => mergeNodeStyle(node.style, deviceMode, element?.type),
    [element?.type, deviceMode, node.style],
  )
  const parentStyle = useMemo(
    () => mergeNodeStyle(parent?.style || {}, deviceMode),
    [deviceMode, parent],
  )

  return (
    <NodeV2Styled
      $element={element}
      $isRoot={false}
      $isSlot={false}
      $node={node}
      $parent={parent}
      $parentStyle={parentStyle || null}
      $style={style}
    >
      {element ? (
        <EditorElementComponent element={element} mode={SKELETON_MODE} isV2 />
      ) : (
        children.map((child) => <Node {...props} key={child.id} node={child} parent={node} />)
      )}
    </NodeV2Styled>
  )
}

const ScaledNode: React.FC<IScaledBlockSkeletonProps> = (props) => {
  return (
    <ErrorBoundary>
      <Scalable maxHeight={192} minWidth={460}>
        <Node {...props} />
      </Scalable>
    </ErrorBoundary>
  )
}

export default React.memo(ScaledNode)
